import Seo from "../components/Seo";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bus, CalendarDays, Clock, FileText, Info, Pencil, Plus, ShieldCheck, Sun, Trash2, Trophy, X } from "lucide-react";
import { API, FEES, IMAGES } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const formatDate = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const ProgramCard = ({ testId, title, time, image, imageClass = "h-64 sm:h-80", split = false, children }) => (
  <FadeIn>
    <article
      className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm h-full ${split ? "md:grid md:grid-cols-5" : ""}`}
      data-testid={testId}
    >
      <img
        src={image}
        alt={title}
        className={split ? `w-full object-cover h-64 md:h-full md:col-span-2 ${imageClass}` : `w-full object-cover ${imageClass}`}
      />
      <div className={`p-8 ${split ? "md:col-span-3" : ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy">{title}</h2>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface px-4 py-2 text-sm font-semibold text-navy">
            <Clock className="h-4 w-4 text-waratah" /> {time}
          </span>
        </div>
        <div className="mt-4 space-y-4 text-base sm:text-lg text-slate-600 leading-relaxed">{children}</div>
      </div>
    </article>
  </FadeIn>
);

const tapSchedule = [
  {
    day: "Monday",
    activities: [
      { name: "Dance", years: "Years 1-6", price: 125, note: "Dance this term focuses on choreographing, pattern recognition and making." },
    ],
  },
  {
    day: "Tuesday",
    activities: [
      { name: "Rhee Taekwondo", years: "Years K-6", price: 250, note: "Rhee Taekwondo supports discipline, respect, focus, fitness, confidence and resilience in a structured environment led by Rhee TKD professionals." },
      { name: "Ceramics", years: "Years 1-6", price: 180, note: "Ceramics supports creativity, patience, fine motor skills and self-expression through hands-on clay techniques with a ceramics specialist from Strathnairn Arts Centre." },
    ],
  },
  {
    day: "Wednesday",
    activities: [],
  },
  {
    day: "Thursday",
    activities: [
      { name: "French Homework", years: "Years K-6", price: 125, note: "French Homework provides a calm, supported space for children to complete homework, practise language skills and build confidence." },
    ],
  },
  {
    day: "Friday",
    activities: [],
  },
];

const SVC_PROGRAM_PDF_URL = "/svc-spring-2026.pdf";
const SVC_CALENDAR_IMAGES = ["/svc-calendar-1.webp", "/svc-calendar-2.webp"];
const SVC_RISK_ASSESSMENTS = [
  { name: "Mountainstrong Rockclimbing", href: "/risk-mountainstrong.pdf" },
  { name: "Moncrieff Park", href: "/risk-moncrieff.pdf" },
  { name: "Ninja Park", href: "/risk-ninjapark.pdf" },
  { name: "Phillip Ice Skating", href: "/risk-iceskating.pdf" },
  { name: "Mulligans Flat", href: "/risk-mulligans.pdf" },
];

const VacationDates = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/vacation-periods`);
      setPeriods(res.data);
    } catch {
      toast.error("Could not load vacation care dates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = () => {
    if (!passcode.trim()) {
      toast.error("Please enter the admin passcode to edit dates.");
      return;
    }
    setDraft(periods.map((p) => ({ ...p })));
    setEditing(true);
  };

  const updateDraft = (idx, field, value) => {
    setDraft((d) => d.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const addPeriod = () => {
    setDraft((d) => [...d, { id: `vd-${Date.now()}`, label: "", start_date: "", end_date: "", notes: "" }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/vacation-periods`,
        { periods: draft.filter((p) => p.label && p.start_date && p.end_date) },
        { headers: { "x-admin-passcode": passcode.trim() } }
      );
      setPeriods(res.data);
      setEditing(false);
      toast.success("Vacation care dates updated.");
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Incorrect admin passcode.");
      } else {
        toast.error("Could not save changes. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-surface p-6 sm:p-8" data-testid="svc-dates-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="inline-flex items-center gap-2 font-display text-xl sm:text-2xl font-semibold text-navy">
          <CalendarDays className="h-6 w-6 text-waratah" /> Upcoming Vacation Care dates
        </h3>
        {!editing && (
          <div className="flex items-center gap-2">
            <label htmlFor="svc-admin-passcode" className="sr-only">Admin passcode</label>
            <input
              id="svc-admin-passcode"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Admin passcode"
              data-testid="svc-admin-passcode-input"
              className="w-40 rounded-full border border-navy-100 bg-white px-4 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-navy"
            />
            <button
              type="button"
              onClick={startEdit}
              data-testid="svc-edit-dates-button"
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-navy px-4 py-2 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            >
              <Pencil className="h-4 w-4" /> Edit dates
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="mt-6 text-slate-600" data-testid="svc-dates-loading">Loading dates...</p>
      ) : editing ? (
        <div className="mt-6 space-y-4" data-testid="svc-dates-editor">
          {draft.map((p, idx) => (
            <div key={p.id} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid={`svc-editor-row-${idx}`}>
              <input
                aria-label="Period label"
                value={p.label}
                onChange={(e) => updateDraft(idx, "label", e.target.value)}
                placeholder="e.g. July School Holidays 2026"
                data-testid={`svc-editor-label-${idx}`}
                className="rounded-lg border border-navy-100 px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-navy lg:col-span-2"
              />
              <input
                aria-label="Start date"
                type="date"
                value={p.start_date}
                onChange={(e) => updateDraft(idx, "start_date", e.target.value)}
                data-testid={`svc-editor-start-${idx}`}
                className="rounded-lg border border-navy-100 px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-navy"
              />
              <input
                aria-label="End date"
                type="date"
                value={p.end_date}
                onChange={(e) => updateDraft(idx, "end_date", e.target.value)}
                data-testid={`svc-editor-end-${idx}`}
                className="rounded-lg border border-navy-100 px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-navy"
              />
              <div className="flex gap-2">
                <input
                  aria-label="Notes"
                  value={p.notes}
                  onChange={(e) => updateDraft(idx, "notes", e.target.value)}
                  placeholder="Notes"
                  data-testid={`svc-editor-notes-${idx}`}
                  className="flex-1 rounded-lg border border-navy-100 px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-navy"
                />
                <button
                  type="button"
                  onClick={() => setDraft((d) => d.filter((_, i) => i !== idx))}
                  aria-label="Remove period"
                  data-testid={`svc-editor-remove-${idx}`}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-waratah transition-colors duration-200 hover:bg-waratah-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={addPeriod}
              data-testid="svc-editor-add-button"
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-navy px-4 py-2 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            >
              <Plus className="h-4 w-4" /> Add period
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="svc-editor-save-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-waratah px-5 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-waratah-600 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              data-testid="svc-editor-cancel-button"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-navy"
            >
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <ul className="mt-6 space-y-4" data-testid="svc-dates-list">
          {periods.map((p, idx) => (
            <li key={p.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`svc-date-item-${idx}`}>
              <p className="font-semibold text-navy text-lg">{p.label}</p>
              <p className="mt-1 text-base text-slate-600">
                {formatDate(p.start_date)} to {formatDate(p.end_date)}
              </p>
              {p.notes && <p className="mt-2 text-sm text-slate-500">{p.notes}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Programs = () => (
  <div data-testid="programs-page">
    <Seo
      title="Programs - Before, After School & Vacation Care | Telopea Park OSHC"
      description="Before School Care, After School Care, the Telopea Activities Program (TAP) and School Vacation Care with excursions around Canberra, in Barton."
      path="/programs"
    />
    <PageHeader
      testId="programs-header"
      eyebrow="Programs"
      title="Care for every part of the school year"
      description="Four connected programs wrap around the school day and the school year - all delivered by the same warm, consistent team of educators."
    />

    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 space-y-10" data-testid="programs-list">
      <ProgramCard testId="program-bsc" title="Before School Care" time="7:30am - 9:00am" image={IMAGES.running} imageClass="h-72 sm:h-96 object-center">
        <p>
          A calm, unhurried start to the day. Children arrive to a quiet, welcoming space with breakfast options, gentle
          activities and time to ease into the morning before being walked to their school lines.
        </p>
        <p className="font-semibold text-navy">
          Permanent ${FEES.bsc.permanent.toFixed(2)} / Casual ${FEES.bsc.casual.toFixed(2)} per session (before CCS).
        </p>
      </ProgramCard>

      <ProgramCard testId="program-asc" title="After School Care" time="3:15pm - 6:00pm" image={IMAGES.playground} imageClass="h-72 sm:h-96 object-center">
        <p>
          The afternoon belongs to the children: afternoon tea, outdoor play, sport, craft, construction, homework
          support and plenty of time with friends. Educators plan from children&rsquo;s interests using the My Time, Our
          Place framework.
        </p>
        <p className="font-semibold text-navy">
          Permanent ${FEES.asc.permanent.toFixed(2)} / Casual ${FEES.asc.casual.toFixed(2)} per session (before CCS).
        </p>
      </ProgramCard>

      <ProgramCard testId="program-svc" title="School Vacation Care" time="8:00am - 6:00pm" image={IMAGES.excursion} imageClass="h-72 sm:h-96 object-[center_55%]">
        <p>
          Full-day holiday programs blending excursions around Canberra with engaging in-house activity days - cooking,
          art, science, sport and themed days. A detailed program is published to enrolled families before each holiday
          period.
        </p>
        <div className="flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 px-4 py-2 text-sm font-semibold text-navy">
            <Bus className="h-4 w-4 text-waratah" /> Excursions (a ${FEES.excursionLevy} excursion levy applies on excursion days)
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 px-4 py-2 text-sm font-semibold text-navy">
            <Sun className="h-4 w-4 text-waratah" /> In-house activity days
          </span>
        </div>
        <p className="font-semibold text-navy">${FEES.svc.amount.toFixed(2)} per session (before CCS).</p>
        <p className="text-sm text-slate-600">A ${FEES.excursionLevy} excursion levy fee will apply on days of the excursion.</p>
        <div data-testid="svc-program-download">
          <a
            href={SVC_PROGRAM_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="svc-program-pdf-link"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-navy px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
          >
            <CalendarDays className="h-4 w-4" /> Spring 2026 Vacation Care program (PDF)
          </a>
        </div>
        <div className="space-y-4" data-testid="svc-calendar-images">
          <p className="font-semibold text-navy">Spring 2026 program calendar</p>
          {SVC_CALENDAR_IMAGES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`Spring 2026 School Vacation Care calendar - page ${i + 1}`}
              loading="lazy"
              className="w-full rounded-2xl border border-navy-100 shadow-sm"
            />
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-surface p-6 sm:p-8" data-testid="svc-risk-assessments">
          <h3 className="inline-flex items-center gap-2 font-display text-xl sm:text-2xl font-semibold text-navy">
            <ShieldCheck className="h-6 w-6 text-waratah" /> Spring 2026 Risk Assessments
          </h3>
          <p className="mt-2 text-base text-slate-600">
            Risk assessments for our Spring 2026 School Vacation Care excursions. Click to view or download each one.
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {SVC_RISK_ASSESSMENTS.map((r) => (
              <li key={r.href}>
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`svc-risk-link-${r.href.replace("/risk-", "").replace(".pdf", "")}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-navy transition-colors duration-200 hover:border-navy hover:bg-navy hover:text-white"
                >
                  <FileText className="h-4 w-4 flex-none text-waratah" />
                  {r.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <VacationDates />
      </ProgramCard>

      <ProgramCard testId="program-tap" title="Telopea Activities Program (TAP)" time="During After School Care" image={IMAGES.tap} imageClass="h-72 sm:h-96 object-center">
        <p>
          The Telopea Activities Program (TAP) offers paid extracurricular activities run on-site during After School
          Care - from languages and music to sport, dance and coding. Children are escorted to and from their activity
          by our educators, so families get one simple pick-up.
        </p>
        <div className="flex items-start gap-3 rounded-2xl border border-waratah-200 bg-waratah-50 p-5" data-testid="tap-asc-requirement">
          <Info className="mt-0.5 h-5 w-5 flex-none text-waratah" />
          <p className="text-sm sm:text-base font-medium text-navy leading-relaxed">
            Please note: children must be booked into After School Care to be able to attend TAP activities.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-surface p-6 sm:p-8" data-testid="tap-schedule">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h3 className="inline-flex items-center gap-2 font-display text-xl sm:text-2xl font-semibold text-navy">
              <Trophy className="h-6 w-6 text-waratah" /> Current TAP program - Term 4 2026
            </h3>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {tapSchedule.map((d) => (
              <div key={d.day} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`tap-day-${d.day.toLowerCase()}`}>
                <p className="text-sm font-bold uppercase tracking-wider text-waratah">{d.day}</p>
                {d.activities.length === 0 ? (
                  <p className="mt-3 text-sm font-semibold text-slate-400">N/A</p>
                ) : (
                  <ul className="mt-3 space-y-4">
                    {d.activities.map((a) => (
                      <li key={a.name}>
                        <p className="font-semibold text-navy">{a.name}</p>
                        <p className="text-sm font-medium text-slate-500">
                          {a.years} - ${a.price} per term
                        </p>
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{a.note}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </ProgramCard>
    </section>
  </div>
);

export default Programs;
